import { useEffect, useRef } from 'react'

function getCentroid(coords, geometryType = 'MultiPolygon') {
  try {
    let sumX = 0
    let sumY = 0
    let count = 0

    if (geometryType === 'MultiPolygon') {
      // Handle MultiPolygon structure
      coords.forEach((polygonGroup) => {
        if (Array.isArray(polygonGroup)) {
          polygonGroup.forEach((polygon) => {
            if (Array.isArray(polygon)) {
              polygon.forEach(([x, y]) => {
                if (typeof x === 'number' && typeof y === 'number') {
                  sumX += x
                  sumY += y
                  count++
                }
              })
            }
          })
        }
      })
    } else if (geometryType === 'Polygon') {
      // Handle Polygon structure
      coords.forEach((polygon) => {
        if (Array.isArray(polygon)) {
          polygon.forEach(([x, y]) => {
            if (typeof x === 'number' && typeof y === 'number') {
              sumX += x
              sumY += y
              count++
            }
          })
        }
      })
    }

    return count ? [sumX / count, sumY / count] : null
  } catch (e) {
    console.error('Error in getCentroid:', e, coords)
    return null
  }
}

function getBoundsFromGeoJSON(geojson) {
  const bounds = new window.google.maps.LatLngBounds()

  geojson.features.forEach((feature) => {
    const coords = feature.geometry.coordinates
    coords.forEach((polygon) => {
      polygon[0].forEach(([lng, lat]) => {
        bounds.extend(new window.google.maps.LatLng(lat, lng))
      })
    })
  })

  return bounds
}

function getFeatureCenter(feature) {
  try {
    const bounds = new window.google.maps.LatLngBounds()
    const coords = feature.geometry.coordinates
    const geometryType = feature.geometry.type

    if (geometryType === 'MultiPolygon') {
      // MultiPolygon: [[[lat, lng], [lat, lng]...], [[lat, lng]...]]
      coords.forEach((polygonGroup) => {
        if (Array.isArray(polygonGroup)) {
          polygonGroup.forEach((polygon) => {
            if (Array.isArray(polygon)) {
              polygon.forEach(([lng, lat]) => {
                if (typeof lng === 'number' && typeof lat === 'number') {
                  bounds.extend(new window.google.maps.LatLng(lat, lng))
                }
              })
            }
          })
        }
      })
    } else if (geometryType === 'Polygon') {
      // Polygon: [[lat, lng], [lat, lng]...]
      coords.forEach((polygon) => {
        if (Array.isArray(polygon)) {
          polygon.forEach(([lng, lat]) => {
            if (typeof lng === 'number' && typeof lat === 'number') {
              bounds.extend(new window.google.maps.LatLng(lat, lng))
            }
          })
        }
      })
    }

    const center = bounds.getCenter()
    return center ? [center.lng(), center.lat()] : null
  } catch (e) {
    console.error('Error in getFeatureCenter:', e, feature)
    return null
  }
}


const NYCNeighborhoodMap = ({ mapInstance, visible, isCityVisible, borough = '', neighborhood = '' }) => {
  const markersRef = useRef([])

  const colorMap = {
    Manhattan: '#f7941d',
    Brooklyn: '#8cc63f',
    Queens: '#fcee21',
    Bronx: '#b49fc8',
    'Staten Island': '#f78da7',
  }

  useEffect(() => {
    if (!mapInstance) return

    const clearMap = () => {
      mapInstance.data.forEach((feature) => {
        mapInstance.data.remove(feature)
      })
      markersRef.current.forEach((marker) => marker.setMap(null))
      markersRef.current = []
      mapInstance.data.setStyle(null)
    }

    const fetchAndRenderNeighborhoods = async (selectedBorough) => {
      try {
        const url = `https://data.cityofnewyork.us/resource/9nt8-h7nd.geojson?boroname=${encodeURIComponent(selectedBorough)}`
        const res = await fetch(url)
        const geojson = await res.json()

        clearMap()
        mapInstance.data.addGeoJson(geojson)

        const bounds = getBoundsFromGeoJSON(geojson)
        mapInstance.fitBounds(bounds)
        mapInstance.data.setStyle({
          fillColor: 'transparent',
          strokeColor: '#f7941d',
          strokeWeight: 4,
          strokeOpacity: 1,
        })

        geojson.features.forEach((feature) => {
          const name = feature.properties.ntaname
          const coords = feature.geometry.coordinates
          const geometryType = feature.geometry.type
          const center = getCentroid(coords, geometryType)

          if (center) {
            const marker = new window.google.maps.Marker({
              position: { lat: center[1], lng: center[0] },
              map: mapInstance,
              label: {
                text: name,
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#ffffff',
                fontFamily: 'Inter, Arial, sans-serif',
              },
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 0,
                strokeWeight: 0,
                fillOpacity: 0,
              },
              zIndex: 1000,
              optimized: false,
            })

            markersRef.current.push(marker)
          }
        })
      } catch (error) {
        console.error('Failed to fetch neighborhoods from borough click:', error)
      }
    }

    const fetchAndRenderMap = async () => {
      try {
        let geojson = null

        if (!visible && !isCityVisible) {
          clearMap()
          return
        }

        if (isCityVisible) {
          const res = await fetch(
            'https://services5.arcgis.com/GfwWNkhOj9bNBqoJ/arcgis/rest/services/NYC_Borough_Boundary/FeatureServer/0/query?where=1=1&outFields=*&outSR=4326&f=geojson'
          )
          geojson = await res.json()
        } else {
          let baseUrl = 'https://data.cityofnewyork.us/resource/9nt8-h7nd.geojson'
          if (borough) {
            baseUrl += `?boroname=${encodeURIComponent(borough)}`
          } else if (neighborhood) {
            baseUrl += `?$q=${encodeURIComponent(neighborhood)}`
          }

          const res = await fetch(baseUrl)
          geojson = await res.json()
        }

        clearMap()

        mapInstance.data.addGeoJson(geojson)

        mapInstance.data.setStyle((feature) => {
          const boro = feature.getProperty('boroname') || feature.getProperty('BoroName')
        
          console.log('Using borough for style:', boro, colorMap[boro]) // Debug
        
          return {
            fillColor: isCityVisible ? colorMap[boro] || '#ccc' : 'transparent',
            fillOpacity: isCityVisible ? 0.3 : 0,
            strokeColor: colorMap[boro] || '#f7941d',
            strokeOpacity: 1,
            strokeWeight: 4,
          }
        })

        if (isCityVisible) {
          geojson.features.forEach((feature) => {
            const boroName = feature.properties.BoroName
            const coords = feature.geometry.coordinates
            const center = getFeatureCenter(feature)
        
            if (center) {
              const marker = new window.google.maps.Marker({
                position: { lat: center[1], lng: center[0] },
                map: mapInstance,
                label: {
                  text: boroName,
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  fontFamily: 'Inter, Arial, sans-serif',
                },
                icon: {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 0,
                  strokeWeight: 0,
                  fillOpacity: 0,
                  labelOrigin: new window.google.maps.Point(0, 0)
                },
                zIndex: 1000,
                optimized: false,
              })
        
              markersRef.current.push(marker)
            }
          })
        
          mapInstance.data.addListener('click', (event) => {
            const boroughName = event.feature.getProperty('BoroName')
            if (boroughName) {
              fetchAndRenderNeighborhoods(boroughName)
            }
          })
        }

        if (!isCityVisible) {
          geojson.features.forEach((feature) => {
            const name = feature.properties.ntaname
            const coords = feature.geometry.coordinates
            const geometryType = feature.geometry.type
            const center = getCentroid(coords, geometryType)

            if (center) {
              const marker = new window.google.maps.Marker({
                position: { lat: center[1], lng: center[0] },
                map: mapInstance,
                label: {
                  text: name,
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#ffffff',
                  fontFamily: 'Inter, Arial, sans-serif',
                },
                icon: {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 0,
                  strokeWeight: 0,
                  fillOpacity: 0,
                },
                zIndex: 1000,
                optimized: false,
              })

              markersRef.current.push(marker)
            }
          })
        }
      } catch (err) {
        console.error('Failed to fetch map data:', err)
      }
    }

    fetchAndRenderMap()

    return () => {
      clearMap()
    }
  }, [visible, isCityVisible, mapInstance, borough, neighborhood])

  return null
}

export default NYCNeighborhoodMap
